import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BookOpen, ChevronDown, ShieldCheck } from 'lucide-react';
import { ErrorState, Skeleton } from '@/components/States';
import { useHelpArticles } from '@/hooks/use-help';
import { formatDateTime } from '@/lib/utils';
import { Roles, type Session } from '@/auth/keycloak';
import type { HelpArticle, HelpAudience } from '@/lib/schemas';

/**
 * The in-app user guide.
 *
 * ── THE ONE THING THIS SCREEN MUST GET RIGHT ─────────────────────────────────
 *
 * `bodyHtml` is injected with `dangerouslySetInnerHTML`, which `security.md` § 5 otherwise bans
 * outright. This is the single documented exception, and it holds only because of what happens on
 * the server: the HTML is sanitised against an allowlist on the way INTO storage by
 * `GuideHtmlSanitiser`, which is a separate and far narrower configuration than the email one — no
 * scripts, no `<style>` blocks, no `style` attributes, no event handlers, `http`/`https`/`mailto`
 * schemes only.
 *
 * Sanitising here instead would protect nobody: anything that is not our client — a script, a
 * different frontend, a future mobile app — would read the same rows unsanitised. The bytes in the
 * column are the bytes that are safe, and that is the property worth having.
 *
 * The rest of the screen is a contents list and a reading pane, because a guide you cannot see the
 * shape of is a guide people give up on.
 */
export function UserGuidePage({ session }: { session: Session }) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const isAdministrator = session.roles.includes(Roles.Admin);
  const [audience, setAudience] = useState<HelpAudience>('staff');

  // Collapsed on a phone, always open from `lg` up. Stacking the full contents list above the article
  // put roughly a screen and a half of links between the top of the page and the first sentence,
  // which is the wrong way round on the device where scrolling costs the most.
  const [isContentsOpen, setIsContentsOpen] = useState(false);

  const { data, isPending, isError, error, refetch } = useHelpArticles(audience);

  const sections = useMemo(() => groupBySection(data ?? []), [data]);

  // The article named in the URL, falling back to the first one. A stale bookmark shows the guide
  // rather than an error — the content it pointed at may simply have been rewritten under a new slug.
  const current = data?.find((article) => article.slug === slug) ?? data?.[0];

  return (
    <div className="px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-heading">User guide</h1>
          <p className="mt-1 text-sm text-muted">
            How this tool works, and who can do what. Corrections go to an administrator.
          </p>
        </div>

        {/* Only administrators see the switch. Everyone else has exactly one guide, and a toggle with
            one option is a control that raises a question it cannot answer. */}
        {isAdministrator && (
          <div
            role="group"
            aria-label="Which guide"
            className="flex gap-1 rounded-md border border-border bg-surface p-1"
          >
            <AudienceButton
              label="Staff"
              icon={BookOpen}
              isSelected={audience === 'staff'}
              onSelect={() => setAudience('staff')}
            />
            <AudienceButton
              label="Administrator"
              icon={ShieldCheck}
              isSelected={audience === 'admin'}
              onSelect={() => setAudience('admin')}
            />
          </div>
        )}
      </div>

      {isAdministrator && audience === 'staff' && (
        <p className="mt-3 text-xs text-muted">
          This is what everybody else sees. Useful when somebody asks you a question — read their
          guide, not yours.
        </p>
      )}

      {isPending ? (
        <GuideSkeleton />
      ) : isError ? (
        <div className="mt-5">
          <ErrorState error={error} onRetry={() => void refetch()} />
        </div>
      ) : current === undefined ? (
        <p className="mt-5 rounded-lg border border-border bg-surface px-4 py-6 text-sm text-muted">
          There are no articles in this guide yet.
        </p>
      ) : (
        <div className="mt-5 flex flex-col gap-5 lg:flex-row">
          {/* Contents first in the DOM as well as on screen: a screen-reader user meets the shape of
              the guide before its content, same as a sighted one. */}
          <nav aria-label="Guide contents" className="lg:w-64 lg:shrink-0">
            <button
              type="button"
              onClick={() => setIsContentsOpen((open) => !open)}
              aria-expanded={isContentsOpen}
              className="flex w-full items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium text-heading lg:hidden"
            >
              Contents
              <ChevronDown
                className={isContentsOpen ? 'h-4 w-4 rotate-180 transition-transform' : 'h-4 w-4 transition-transform'}
                aria-hidden="true"
              />
            </button>

            <ol
              className={
                isContentsOpen
                  ? 'mt-3 flex flex-col gap-4 lg:mt-0'
                  : 'hidden flex-col gap-4 lg:flex'
              }
            >
              {sections.map(([section, articles]) => (
                <li key={section}>
                  <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
                    {section}
                  </h2>

                  <ol className="flex flex-col">
                    {articles.map((article) => (
                      <li key={article.slug}>
                        <button
                          type="button"
                          // Replaces rather than pushes: a reader who clicked through six articles
                          // wants Back to leave the guide, not to walk back through all six.
                          onClick={() => {
                            void navigate(`/user-guide/${article.slug}`, { replace: true });
                            // Closes on a phone so the article is the next thing on screen. A no-op
                            // at `lg`, where the list is not collapsible in the first place.
                            setIsContentsOpen(false);
                          }}
                          aria-current={article.slug === current.slug ? 'page' : undefined}
                          className={
                            article.slug === current.slug
                              ? 'w-full rounded px-2 py-1.5 text-left text-sm font-medium text-heading bg-chip'
                              : 'w-full rounded px-2 py-1.5 text-left text-sm text-muted hover:bg-chip hover:text-foreground'
                          }
                        >
                          {article.title}
                        </button>
                      </li>
                    ))}
                  </ol>
                </li>
              ))}
            </ol>
          </nav>

          <Article article={current} />
        </div>
      )}
    </div>
  );
}

function Article({ article }: { article: HelpArticle }) {
  const heading = useRef<HTMLHeadingElement>(null);

  // Moving focus to the heading on change is what makes the contents list usable by keyboard: without
  // it, activating a link leaves focus in the sidebar and a screen reader never announces that the
  // reading pane changed at all.
  useEffect(() => {
    heading.current?.focus();
  }, [article.slug]);

  return (
    <article className="min-w-0 flex-1 rounded-lg border border-border bg-surface p-5">
      <h2
        ref={heading}
        tabIndex={-1}
        className="text-lg font-semibold text-heading focus:outline-none focus-visible:outline-2"
      >
        {article.title}
      </h2>

      <p className="mt-1 text-xs text-muted">
        {article.updatedBy === null
          ? `Last updated ${formatDateTime(article.updatedAt)}`
          : `Last updated ${formatDateTime(article.updatedAt)} by ${article.updatedBy}`}
      </p>

      {/* The one documented exception to the ban in security.md § 5 — see the file comment for why
          this is safe and why sanitising here instead would not be. `react/no-danger` is not
          enabled in this repo, so there is no suppression to write; if it is ever turned on, this is
          the single call site that needs one. */}
      <div
        className="guide-prose mt-4 text-sm"
        dangerouslySetInnerHTML={{ __html: article.bodyHtml }}
      />
    </article>
  );
}

function AudienceButton({
  label,
  icon: Icon,
  isSelected,
  onSelect,
}: {
  label: string;
  icon: typeof BookOpen;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      className={
        isSelected
          ? 'flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium text-heading bg-chip'
          : 'flex items-center gap-1.5 rounded px-2.5 py-1 text-xs text-muted hover:bg-chip'
      }
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {label}
    </button>
  );
}

/**
 * Sections in the order their first article appears, articles in server order.
 *
 * Keyed on a Map rather than grouping consecutive runs: if two articles in the same section ever end
 * up with sort orders that straddle another section's, they join the existing heading instead of
 * producing a second one with the same name. A duplicated heading in a contents list reads as a bug
 * in the guide, which is the last thing a guide can afford.
 */
function groupBySection(articles: readonly HelpArticle[]): [string, HelpArticle[]][] {
  const sections = new Map<string, HelpArticle[]>();

  for (const article of articles) {
    const existing = sections.get(article.section);

    if (existing === undefined) {
      sections.set(article.section, [article]);
    } else {
      existing.push(article);
    }
  }

  return [...sections];
}

function GuideSkeleton() {
  return (
    <div className="mt-5 flex flex-col gap-5 lg:flex-row">
      <div className="flex flex-col gap-2 lg:w-64 lg:shrink-0">
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <Skeleton key={index} className="h-7" />
        ))}
      </div>

      <Skeleton className="h-96 min-w-0 flex-1" />
    </div>
  );
}
